import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, X, Apple, Smartphone } from "lucide-react";
import { Button } from "./ui/button";

/**
 * PWA install banner.
 * - Android/Chrome: shows when beforeinstallprompt fires
 * - iOS Safari: shows manual instructions (since iOS doesn't expose a prompt event)
 * Dismissed state is persisted in localStorage.
 */
export default function InstallBanner() {
  const { i18n } = useTranslation();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("kanama_install_dismissed") === "1") return;
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) return;

    const ua = window.navigator.userAgent.toLowerCase();
    const ios = /iphone|ipad|ipod/.test(ua) && !window.MSStream;
    if (ios) {
      setIsIOS(true);
      setVisible(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem("kanama_install_dismissed", "1");
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;

  const isAr = i18n.language === "ar";
  const messages = {
    fr: {
      title: "Installer Kanama",
      subtitle: "Accédez à votre annuaire du Tchad en un tap, même hors connexion.",
      cta: "Installer",
      iosHelp: "Sur iPhone : touchez « Partager » puis « Sur l'écran d'accueil »",
    },
    en: {
      title: "Install Kanama",
      subtitle: "Access your guide to Chad in one tap, even offline.",
      cta: "Install",
      iosHelp: "On iPhone: tap Share, then Add to Home Screen",
    },
    ar: {
      title: "ثبّت كناما",
      subtitle: "الوصول إلى دليل تشاد بنقرة واحدة، حتى دون اتصال.",
      cta: "تثبيت",
      iosHelp: "على iPhone: اضغط على «مشاركة» ثم «إضافة إلى الشاشة الرئيسية»",
    },
    es: {
      title: "Instalar Kanama",
      subtitle: "Accede a tu guía de Chad con un toque, incluso sin conexión.",
      cta: "Instalar",
      iosHelp: "En iPhone: toca Compartir y luego Añadir a la pantalla de inicio",
    },
    zh: {
      title: "安装 Kanama",
      subtitle: "一键访问您的乍得指南，即使离线也可使用。",
      cta: "安装",
      iosHelp: "iPhone：点击「分享」→「添加到主屏幕」",
    },
  };
  const lang = messages[i18n.language] || messages.fr;

  return (
    <div
      className="fixed bottom-3 left-3 right-3 sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm z-40 rounded-2xl bg-white border border-brand-line shadow-[0_24px_60px_rgba(45,42,38,0.18)] p-4 flex items-start gap-3"
      data-testid="install-banner"
    >
      <div className="w-10 h-10 rounded-xl bg-brand-gold flex items-center justify-center shrink-0 text-brand-black">
        {isIOS ? <Apple className="w-5 h-5" /> : <Smartphone className="w-5 h-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-semibold text-brand-ink text-sm leading-tight mb-1">
          {lang.title}
        </div>
        <p className={`text-xs text-brand-inkSoft leading-relaxed ${isAr ? "text-right" : ""}`}>
          {lang.subtitle}
        </p>
        {isIOS ? (
          <p className="text-[11px] text-brand-inkMuted mt-2 leading-relaxed">
            {lang.iosHelp}
          </p>
        ) : (
          <Button
            onClick={install}
            size="sm"
            className="mt-2 rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover text-white"
            data-testid="install-cta"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" />
            {lang.cta}
          </Button>
        )}
      </div>
      <button
        onClick={dismiss}
        className="text-brand-inkMuted hover:text-brand-ink p-1 -m-1 rounded-full"
        aria-label="Close"
        data-testid="install-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
